// src/pages/LevelSelectionPage.jsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import LevelTile from "../components/recording/LevelTile";
import { useAccessControl } from "../hooks/useAccessControl";
import { Link } from "react-router-dom";
import BottomInfoSheet from "../components/info/BottomInfoSheet";
import Spacer from "../components/ui/Spacer";

export default function LevelSelectionPage() {
  const { t } = useTranslation();
  const [showInfo, setShowInfo] = useState(false);

  // ✅ pull loading flag from access control
  const {
    loading: acLoading,
    plan,
    canRecord,
    reason,
    upgradeTarget,
    totalAny,
  } = useAccessControl();

  const isNoPlan = plan === "none";
  const firstRecordLeft = isNoPlan && totalAny === 0;

  // ✅ Optimistic allow: while loading, enable both tiles
  const allowExertion = true;
  // acLoading || (isNoPlan && firstRecordLeft) || !!canRecord.exertion;
  const allowSpirometry = true;
  // acLoading || (isNoPlan && firstRecordLeft) || !!canRecord.spirometry;

  const disabledMsg =
    reason === "cooldown"
      ? t("gate.cooldown", {
          defaultValue: "Freemium limit reached. Try again next month.",
        })
      : t("gate.quota", {
          defaultValue: "Monthly limit reached on your plan.",
        });

  // Show an upsell banner when user has a plan and we know they're blocked.
  const isQuotaBlocked = false;
  // !acLoading &&
  // !isNoPlan &&
  // !!reason &&
  // (!canRecord.exertion || !canRecord.spirometry);

  return (
    <div className="relative w-full max-w-2xl h-full flex flex-col overflow-y-scroll p-4 gap-4 z-10 mx-auto">
      {/* Info tab/button */}

      <button
        type="button"
        onClick={() => setShowInfo(true)}
        className="w-full text-left flex items-center justify-between"
        aria-label={t("info.bestRecording.title", {
          defaultValue: "How to take a perfect recording",
        })}
      >
        <div className="flex flex-col">
          <span className="subtitle-text">
            {t("info.bestRecording.title", {
              defaultValue: "How to take a perfect recording",
            })}
          </span>
          <span className="muted-text">
            {t("levelSelection.perfectTabHint", {
              defaultValue: "Tap to learn how to take a perfect recording",
            })}
          </span>
        </div>
        <span className="material-symbols-outlined" aria-hidden="true">
          info
        </span>
      </button>
      <Spacer />
      {/* Banner for first-time (no plan) users — only after loading finishes */}
      {!acLoading && isNoPlan && (
        <div className="rounded-2xl flex flex-col gap-4 h-fit items-start">
          {firstRecordLeft ? (
            <h3 className="subtitle-text">
              {t("gate.firstFree", {
                defaultValue:
                  "You can do 1 free recording before choosing a plan.",
              })}
            </h3>
          ) : (
            <>
              <h3 className="subtitle-text">
                {t("gate.firstUsed", {
                  defaultValue:
                    "You have used your first free recording, to continue, upgrade your account.",
                })}
              </h3>
              <div className="flex justify-end w-full">
                <Link
                  to="/payment"
                  className="primary-button px-4 py-2 text-sm"
                >
                  {t("upsell.choosePlan", { defaultValue: "Choose a plan" })}
                </Link>
              </div>
            </>
          )}
        </div>
      )}

      {/* Quota/cooldown banner for users on a plan — only after loading */}
      {isQuotaBlocked && (
        <div className="rounded-2xl flex flex-col gap-3 h-fit items-start">
          <h3 className="subtitle-text">{disabledMsg}</h3>
          <p className="text-sm text-typography">
            {t("gate.upgradeCopy", {
              defaultValue:
                "Upgrade your plan to unlock more recordings each month and deeper insights.",
            })}
          </p>
          <div className="flex justify-end w-full">
            <Link to="/payment" className="primary-button px-4 py-2 text-sm">
              {t("upsell.upgrade", {
                defaultValue: upgradeTarget
                  ? `Upgrade to ${upgradeTarget}`
                  : "Upgrade plan",
              })}
            </Link>
          </div>
        </div>
      )}

      {/* Tiles */}
      <div className="w-full max-w-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
        <LevelTile
          to={allowExertion ? "/recording/exertion-recovery/guide" : "/payment"}
          title={t("levelSelection.etTitle", {
            defaultValue: "Exertion Recovery",
          })}
          desc={t("levelSelection.etDesc", {
            defaultValue: "Track how your breathing recovers after activity.",
          })}
          disabled={!allowExertion}
          disabledReason={
            !allowExertion && !acLoading ? disabledMsg : undefined
          }
          cta={t("levelSelection.cta", {
            defaultValue: allowExertion ? "Continue" : "Upgrade",
          })}
          meta={[
            t("levelSelection.meta.time1", { defaultValue: "~7 min" }),
            t("levelSelection.meta.inApp", { defaultValue: "Guided · In-app" }),
            t("levelSelection.meta.outputRecovery1", {
              defaultValue: "Recovery Curve",
            }),
            t("levelSelection.meta.outputRecovery2", {
              defaultValue: "Breathe Rate",
            }),
            t("levelSelection.meta.outputRecovery3", {
              defaultValue: "Recovery Time",
            }),
            t("levelSelection.meta.outputRecovery4", {
              defaultValue: "Breathing Recovery",
            }),
          ]}
        />

        <LevelTile
          to={
            allowSpirometry ? "/recording/forced-spirometry/guide" : "/payment"
          }
          title={t("levelSelection.fsTitle", {
            defaultValue: "Lung Fitness & Age Test",
          })}
          desc={t("levelSelection.fsDesc", {
            defaultValue:
              "One deep breath out at full effort. Get simple airflow metrics and your estimated lung age.",
          })}
          disabled={!allowSpirometry}
          disabledReason={
            !allowSpirometry && !acLoading ? disabledMsg : undefined
          }
          cta={t("levelSelection.cta", {
            defaultValue: allowSpirometry ? "Continue" : "Upgrade",
          })}
          meta={[
            t("levelSelection.meta.time2", { defaultValue: "~1-2 min" }),
            t("levelSelection.meta.inApp", { defaultValue: "Guided · In-app" }),
            // t("levelSelection.meta.outputLungAge1", {
            //   defaultValue: "Lung Age",
            // }),
            t("levelSelection.meta.outputLungAge2", {
              defaultValue: "Lung Score",
            }),
          ]}
        />
      </div>
      {/* Bottom sheet with the actual content */}
      <BottomInfoSheet
        open={showInfo}
        onClose={() => setShowInfo(false)}
        infoKey="info.bestRecording"
        titleFallback={t("info.bestRecording.title", {
          defaultValue: "How to take a perfect recording",
        })}
      />
    </div>
  );
}
